import { create } from 'zustand'
import { components, operations } from '@/lib/types/api-types'
import { api, queryClient } from '@/lib/api-client.ts'
import { toast } from '@/lib/hooks/useToast.ts'
import i18n from '@/i18n.ts'
import { clearMercureToken, closeAllNativeEventSources } from '@/lib/helpers/subscribeToMercureTopic'
import { pausePersist, resumePersist } from '@/lib/queryPersister'
import { clearVisitedPaths } from '@/lib/helpers/visitedPaths'
import { clearCachedAttachments } from '@/lib/prefetch/offlineAttachmentCache'
export type User = components['schemas']['User-user.me']
export type OrganizationMembership = components['schemas']['OrganizationMember.jsonld-user.me']
export type Organization = components['schemas']['Organization-organization.read']
export type OrganizationRole =
  operations['api_organization_roles_get_collection']['responses']['200']['content']['application/ld+json']
export type LoginData = {
  email: string
  password: string
}

type UserStoreState = {
  user: User | null
  setUser: (user: User) => void
  organizationRoles: OrganizationRole | null
  setOrganizationRoles: (roles: OrganizationRole) => void
  currentOrganizationMembership: OrganizationMembership | null
  setCurrentOrganizationMembership: (organizationMembership: OrganizationMembership) => void
  currentOrganization: Organization | null
  loading: boolean
  loginUser: (data: LoginData) => Promise<User | null>
  logoutUser: () => void
  setLoading: (loading: boolean) => void
  setCurrentOrganization: (organization: Organization) => void
}

export const useUserStore = create<UserStoreState>(set => ({
  user: null,
  setUser: (user: User) => {
    set({ user })
  },
  organizationRoles: null,
  setOrganizationRoles: (roles: OrganizationRole) => {
    set({ organizationRoles: roles })
  },
  currentOrganizationMembership: null,
  setCurrentOrganizationMembership: (organizationMembership: OrganizationMembership) => {
    set({ currentOrganizationMembership: organizationMembership })
  },
  currentOrganization: null,
  loading: true,
  setLoading: (loading: boolean) => {
    set({ loading })
  },

  loginUser: async (data: LoginData) => {
    const res = await api.post('/login', data)
    const user: User = res?.data

    if (user) {
      // Clear all cached data from the previous session to prevent data leakage
      // between users on shared devices. Uses clear() (synchronous, no refetch)
      // instead of invalidateQueries() which triggered a thundering herd —
      // refetching hundreds of prefetched queries simultaneously, causing
      // WKWebView OOM on iOS.
      // Pause persistence to prevent PersistQueryClientProvider from dehydrating
      // the shrinking cache on each individual query removal during clear().
      pausePersist()
      queryClient.clear()
      await resumePersist()
      set({ user, loading: false })
      if (user.organizationMemberships) {
        const organizationMembership: OrganizationMembership | undefined =
          user.organizationMemberships.find(
            // @ts-expect-error @id is there
            memberShip => memberShip?.['@id'] === user.activeMembership,
          )
        if (organizationMembership) {
          set({ currentOrganizationMembership: organizationMembership })
        } else {
          set({ currentOrganizationMembership: user.organizationMemberships?.[0] })
        }
      }
      return user
    }
    toast({
      variant: 'destructive',
      title: i18n.t('common.error'),
      // @ts-expect-error - axios types are not correct
      description: res?.response?.data?.error,
    })
    set({ loading: false })
    return null
  },

  setCurrentOrganization: (organization: Organization) => {
    set({ currentOrganization: organization })
  },
  logoutUser: () => {
    set({ user: null, loading: false })
    api.get('/logout')
    // Pause persistence to suppress dehydration events during clear().
    // Resume is fire-and-forget — the cache is empty so the flush is trivial.
    pausePersist()
    queryClient.clear()
    resumePersist()
    // Clear Mercure token and close all EventSources to prevent data leak
    clearMercureToken()
    closeAllNativeEventSources()
    clearVisitedPaths()
    clearCachedAttachments()
  },
}))
